import React, { useState, useEffect } from 'react';
import { logger } from '@/lib/logger';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Split, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useOrganization } from '@clerk/clerk-react';
import { createSplitConfigService, type SplitItem } from '@/services/portalio/splitConfigService';
import { CategoryDropdown } from './CategoryDropdown';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';

interface SplitConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: {
    id: string;
    reference_id: string;
    portal_product_id?: string;
    portal_name: string;
    brand?: string;
    model?: string;
    category?: string;
    quantity?: number;
  };
  existingConfig?: SplitItem[];
  onSave?: () => void;
  floorPlanId?: string;
}

export function SplitConfigModal({
  isOpen,
  onClose,
  product,
  existingConfig,
  onSave,
  floorPlanId,
}: SplitConfigModalProps) {
  const { organization } = useOrganization();
  const [splitItems, setSplitItems] = useState<SplitItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showRemoveWarning, setShowRemoveWarning] = useState(false);
  const [placedItemsInfo, setPlacedItemsInfo] = useState<{
    count: number;
    rooms: string[];
  }>({ count: 0, rooms: [] });

  // Initialize with existing config or default splits
  useEffect(() => {
    if (existingConfig && existingConfig.length > 0) {
      setSplitItems(existingConfig);
    } else if (isOpen) {
      // Default splits based on quantity or common patterns
      const defaultSplits = createDefaultSplits(product);
      setSplitItems(defaultSplits);
    }
  }, [existingConfig, isOpen, product]);

  const createDefaultSplits = (product: any): SplitItem[] => {
    // Always default to numeric suffixes for any product type
    // Users can rename to L/R or any other suffix they prefer
    return [
      {
        id: crypto.randomUUID(),
        suffix: '1',
        quantity: 1,
        category: product.category,
        subcategory: product.subcategory,
      },
      {
        id: crypto.randomUUID(),
        suffix: '2',
        quantity: 1,
        category: product.category,
        subcategory: product.subcategory,
      },
    ];
  };

  const handleAddSplit = () => {
    const newSplit: SplitItem = {
      id: crypto.randomUUID(),
      suffix: String(splitItems.length + 1),
      quantity: 1,
      category: product.category,
      subcategory: (product as any).subcategory,
    };
    setSplitItems([...splitItems, newSplit]);
  };

  const handleRemoveSplit = (id: string) => {
    if (splitItems.length <= 2) {
      toast.error('Minimum 2 split items required');
      return;
    }
    setSplitItems(splitItems.filter(item => item.id !== id));
  };

  const handleUpdateSplit = (id: string, updates: Partial<SplitItem>) => {
    setSplitItems(splitItems.map(item => (item.id === id ? { ...item, ...updates } : item)));
  };

  // Check for placed items that will be affected
  const checkPlacedItems = async () => {
    if (!floorPlanId || !organization?.id) return { count: 0, rooms: [] };

    try {
      // Get all placed items for this product
      logger.log('Checking placed items for product:', product);

      // First get all products for this floor plan
      const supabase = await getAuthenticatedSupabaseClient();
      const { data: allProducts, error } = await supabase
        .from('lv_products')
        .select('id, room_id, metadata')
        .eq('floor_plan_id', floorPlanId);

      if (error) {
        logger.error('Error fetching products:', error);
        return { count: 0, rooms: [] };
      }

      // Filter products that match our criteria
      const placedItems =
        allProducts?.filter(item => {
          const metadata = item.metadata || {};

          // Check if this is a split item from the same parent
          if (metadata.split_parent_reference === product.reference_id) {
            return true;
          }

          // Check if this is the original product (non-split)
          if (
            product.portal_product_id &&
            metadata.portal_product_id === product.portal_product_id
          ) {
            // Only count if it's NOT a split item (to avoid double counting)
            return !metadata.is_split;
          }

          return false;
        }) || [];

      logger.log('Found placed items:', placedItems);

      // Get room names
      const roomIds = [...new Set(placedItems.map(item => item.room_id).filter(Boolean))];
      let roomNames: string[] = [];

      if (roomIds.length > 0) {
        const { data: rooms } = await supabase.from('rooms').select('name').in('id', roomIds);

        roomNames = rooms?.map(r => r.name) || [];
      }

      return {
        count: placedItems.length,
        rooms: roomNames,
      };
    } catch (error) {
      logger.error('Error checking placed items:', error);
      return { count: 0, rooms: [] };
    }
  };

  // Delete placed items when configuration changes
  const deletePlacedItems = async () => {
    if (!floorPlanId || !organization?.id) return;

    try {
      logger.log('Deleting placed items for product:', product);

      // First get all products for this floor plan
      const supabase = await getAuthenticatedSupabaseClient();
      const { data: allProducts, error: fetchError } = await supabase
        .from('lv_products')
        .select('id, metadata')
        .eq('floor_plan_id', floorPlanId);

      if (fetchError) {
        logger.error('Error fetching products:', fetchError);
        toast.error('Failed to fetch placed items');
        return;
      }

      // Filter products that match our criteria
      const itemsToDelete =
        allProducts?.filter(item => {
          const metadata = item.metadata || {};

          // Check if this is a split item from the same parent
          if (metadata.split_parent_reference === product.reference_id) {
            return true;
          }

          // Check if this is the original product (non-split)
          if (
            product.portal_product_id &&
            metadata.portal_product_id === product.portal_product_id
          ) {
            // Only delete if it's NOT a split item (to avoid double deletion)
            return !metadata.is_split;
          }

          return false;
        }) || [];

      if (itemsToDelete.length === 0) {
        logger.log('No items to delete');
        return;
      }

      // Delete the filtered items
      const itemIds = itemsToDelete.map(item => item.id);
      const { error: deleteError, count } = await supabase
        .from('lv_products')
        .delete()
        .in('id', itemIds);

      if (deleteError) {
        logger.error('Error deleting placed items:', deleteError);
        toast.error('Failed to remove placed items');
      } else {
        logger.log(`Removed ${count} placed items for product ${product.portal_name}`);
        if (count && count > 0) {
          toast.success(`Removed ${count} item${count > 1 ? 's' : ''} from floor plan`);
        }
      }
    } catch (error) {
      logger.error('Error deleting placed items:', error);
      toast.error('Failed to remove placed items');
    }
  };

  const handleSaveClick = async () => {
    logger.log('handleSaveClick - checking for placed items');
    // Check for placed items first
    const placedInfo = await checkPlacedItems();
    logger.log('Placed items info:', placedInfo);

    if (placedInfo.count > 0) {
      logger.log('Found placed items, showing warning');
      setPlacedItemsInfo(placedInfo);
      setShowWarning(true);
    } else {
      // No placed items, proceed directly
      logger.log('No placed items, proceeding with save');
      await handleSave();
    }
  };

  const handleRemoveSplitClick = async () => {
    logger.log('handleRemoveSplitClick - checking for placed items');
    // Check for placed items first
    const placedInfo = await checkPlacedItems();
    logger.log('Placed items info:', placedInfo);

    setPlacedItemsInfo(placedInfo);
    setShowRemoveWarning(true);
  };

  const handleRemoveConfiguration = async () => {
    if (!organization?.id) {
      toast.error('Organization not found');
      return;
    }

    setSaving(true);
    setShowRemoveWarning(false);

    try {
      // First delete placed items
      await deletePlacedItems();

      // Then remove the split configuration
      const splitService = createSplitConfigService(organization.id);
      const success = await splitService.deleteSplitConfig(product.reference_id);

      if (success) {
        toast.success('Split configuration removed');
        // Call onSave callback to refresh the parent component
        onSave?.();
        // Close the modal
        onClose();
      } else {
        toast.error('Failed to remove split configuration');
      }
    } catch (error) {
      logger.error('Error removing split config:', error);
      toast.error('Failed to remove split configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!organization?.id) {
      toast.error('Organization not found');
      return;
    }

    // Validate splits
    if (splitItems.length < 2) {
      toast.error('At least 2 split items are required');
      return;
    }

    // Check for duplicate suffixes
    const suffixes = splitItems.map(item => item.suffix);
    if (new Set(suffixes).size !== suffixes.length) {
      toast.error('Split items must have unique suffixes');
      return;
    }

    setSaving(true);
    try {
      const splitService = createSplitConfigService(organization.id);
      const success = await splitService.saveSplitConfig(
        product.reference_id,
        product.brand || 'Unknown',
        product.model || product.portal_name,
        splitItems,
        product.portal_product_id,
      );

      if (success) {
        // Always try to delete placed items when config changes
        // This ensures we clean up even if the check didn't find them
        await deletePlacedItems();

        toast.success('Split configuration saved');

        // Call onSave callback to refresh the parent component
        // This ensures the new split configuration is loaded
        await onSave?.();

        // Close the modal after everything is done
        onClose();
      } else {
        toast.error('Failed to save split configuration');
      }
    } catch (error) {
      logger.error('Error saving split config:', error);
      toast.error('Failed to save split configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={open => {
          if (!open && !saving) {
            onClose();
          }
        }}
      >
        <DialogContent
          className="max-w-2xl max-h-[80vh] overflow-y-auto"
          onEscapeKeyDown={e => {
            if (!saving) {
              onClose();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Split className="h-5 w-5" />
              Configure Split: {product.portal_name}
            </DialogTitle>
            <DialogDescription>
              Define how this product should be split into individual items. This configuration will
              apply to all matching products imported by your team.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 my-4">
            {/* Product Info */}
            <div className="bg-muted/50 rounded-lg p-3 space-y-1">
              <div className="flex items-center gap-4 text-sm">
                <span className="text-muted-foreground">Reference ID:</span>
                <Badge variant="outline">{product.reference_id}</Badge>
              </div>
              {product.brand && product.model && (
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground">Product:</span>
                  <span>
                    {product.brand} • {product.model}
                  </span>
                </div>
              )}
            </div>

            {/* Split Items */}
            <div className="space-y-3">
              <Label>Split Items</Label>
              <div className="space-y-2">
                {splitItems.map((item, index) => (
                  <div key={item.id} className="border rounded-lg p-4">
                    <div className="flex items-end gap-3">
                      <div className="w-20">
                        <label className="text-xs text-muted-foreground mb-1 block">Suffix</label>
                        <Input
                          placeholder="1/2/3"
                          value={item.suffix}
                          onChange={e => handleUpdateSplit(item.id, { suffix: e.target.value })}
                          onKeyDown={e => e.stopPropagation()}
                          onKeyUp={e => e.stopPropagation()}
                          className="font-mono"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs text-muted-foreground mb-1 block">
                          Name (optional)
                        </label>
                        <Input
                          placeholder={`Default: ${item.suffix} - ${product.model || product.portal_name}`}
                          value={item.name || ''}
                          onChange={e => handleUpdateSplit(item.id, { name: e.target.value })}
                          onKeyDown={e => e.stopPropagation()}
                          onKeyUp={e => e.stopPropagation()}
                        />
                      </div>
                      <CategoryDropdown
                        value={
                          item.category || item.subcategory
                            ? {
                                category: item.category || product.category || 'other',
                                subcategory: item.subcategory,
                              }
                            : null
                        }
                        onChange={selection => {
                          if (selection) {
                            handleUpdateSplit(item.id, {
                              category: selection.category,
                              subcategory: selection.subcategory,
                            });
                          } else {
                            handleUpdateSplit(item.id, {
                              category: undefined,
                              subcategory: undefined,
                            });
                          }
                        }}
                        placeholder="Category"
                        layout="horizontal"
                        displayMode="icon"
                        showLabels={true}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveSplit(item.id)}
                        disabled={splitItems.length <= 2}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <Button variant="outline" size="sm" onClick={handleAddSplit} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Split Item
              </Button>
            </div>

            {/* Help Text */}
            <div className="text-sm text-muted-foreground bg-blue-50/50 dark:bg-blue-950/20 rounded-lg p-3">
              <p className="font-medium mb-1">How splits work:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Each split item becomes a separate draggable product</li>
                <li>Suffixes identify each split (e.g., 1, 2, 3...)</li>
                <li>Categories can be customized per split item</li>
                <li>This configuration applies to all team members automatically</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="flex justify-between">
            <div className="flex-1">
              {existingConfig && existingConfig.length > 0 && (
                <Button
                  variant="destructive"
                  onClick={handleRemoveSplitClick}
                  disabled={saving}
                  className="mr-auto"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove Split Configuration
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSaveClick} disabled={saving || splitItems.length < 2}>
                {saving ? 'Saving...' : 'Save Split Configuration'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warning Dialog */}
      <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Warning: Placed Items Will Be Removed
            </AlertDialogTitle>
            <AlertDialogDescription>
              {existingConfig && existingConfig.length > 0
                ? 'Modifying this split configuration will remove all placed split items from the current floor plan.'
                : 'Adding a split configuration will remove all placed instances of this product from the current floor plan.'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 mt-4">
            {placedItemsInfo.count > 0 && (
              <div className="bg-yellow-50 dark:bg-yellow-950/20 rounded-lg p-3 border border-yellow-200 dark:border-yellow-800">
                <p className="font-medium text-sm">
                  {placedItemsInfo.count} {placedItemsInfo.count === 1 ? 'item' : 'items'} will be
                  removed
                </p>
                {placedItemsInfo.rooms.length > 0 && (
                  <p className="text-sm mt-1">From rooms: {placedItemsInfo.rooms.join(', ')}</p>
                )}
              </div>
            )}

            <p className="text-sm font-medium">This action cannot be undone.</p>

            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-1">Note:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Other floor plans will not be affected</li>
                <li>The new configuration will apply to all future imports</li>
                <li>You'll need to re-place the split items on the floor plan</li>
              </ul>
            </div>
          </div>

          <AlertDialogFooter className="mt-6">
            <AlertDialogCancel onClick={() => setShowWarning(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                setShowWarning(false);
                await handleSave();
              }}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              Remove Items & Save Configuration
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Split Configuration Warning Dialog */}
      <AlertDialog open={showRemoveWarning} onOpenChange={setShowRemoveWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Remove Split Configuration
            </AlertDialogTitle>
            <AlertDialogDescription>
              Removing the split configuration will restore this product to its default state (no
              splits).
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 mt-4">
            {placedItemsInfo.count > 0 && (
              <>
                <p>This will remove all placed split items from the current floor plan:</p>
                <div className="bg-red-50 dark:bg-red-950/20 rounded-lg p-3 border border-red-200 dark:border-red-800">
                  <p className="font-medium text-sm">
                    {placedItemsInfo.count} {placedItemsInfo.count === 1 ? 'item' : 'items'} will be
                    removed
                  </p>
                  {placedItemsInfo.rooms.length > 0 && (
                    <p className="text-sm mt-1">From rooms: {placedItemsInfo.rooms.join(', ')}</p>
                  )}
                </div>
              </>
            )}

            <p className="text-sm font-medium">This action cannot be undone.</p>

            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-1">Note:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Other floor plans will not be affected</li>
                <li>The product will return to its original non-split state</li>
                <li>You'll need to re-place the product on the floor plan if needed</li>
              </ul>
            </div>
          </div>

          <AlertDialogFooter className="mt-6">
            <AlertDialogCancel onClick={() => setShowRemoveWarning(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveConfiguration}
              className="bg-red-600 hover:bg-red-700"
            >
              Remove Configuration
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
